import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SelectorOptions } from 'shared/design-system/components/Selector';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactTextIndicator from 'shared/design-system/components/TrueImpactTextIndicator';
import { useClient } from 'shared/apollo';
import { AnswerInput, useProgram } from 'shared/providers/ProgramProvider';
import {
  parseReachIndicatorNames,
  REACH_INDICATORS_UNSAVED_MARKER,
} from 'shared/providers/ProgramProvider/utils';
import { updateSuccessCriteria } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/api';
import { Indicator } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import CardGuide from 'npo/pages/ReportBuilder/Components/ImpactIndicator/CardGuide';
import { getExampleText } from 'npo/pages/ReportBuilder/Components/ImpactSummary/IndicatorGhostText';
import { saveReportIndicators } from 'npo/pages/ReportBuilder/Components/IndicatorDetails/api';
import { ReportsTable } from '../ReportGroup';
import SelectorField from './SelectorField';
import { useBlockedSignal } from './useBlockedSignal';

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
`;

const EntryCard = styled.div`
  width: 700px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.tiny};
  padding: ${SPACERS.small};
  border: 1px solid ${COLORS.grayLight};
  border-radius: 4px;
  background-color: ${COLORS.whiteBase};
  box-sizing: border-box;

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  input,
  textarea {
    font: inherit;
    padding: ${SPACERS.tiny} ${SPACERS.small};
    border: 1px solid ${COLORS.grayBase};
    border-radius: 4px;
    background-color: ${COLORS.whiteBase};
    &:focus {
      outline: none;
      border-color: ${COLORS.ctaPrimary};
    }
    &:disabled {
      background-color: ${COLORS.grayLightest};
      color: ${COLORS.greyDark};
    }
  }

  textarea {
    min-height: 80px;
    resize: vertical;
  }
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SUCCESS_CRITERIA_MAX_LENGTH = 250;

const GUIDE_PADDING = `padding-left: ${SPACERS.large}; padding-right: ${SPACERS.small};`;

interface ReachIndicatorsFieldProps {
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
  report: ReportsTable;
  onBlockedChange?: (blocked: boolean) => void;
}

interface IndicatorOption {
  indicatorId: number;
  name: string;
  indicator: Indicator;
}

interface EntryDetails {
  successCriteria: string;
  forecast: string;
}

const serializeNames = (names: string[]): string =>
  names.length ? JSON.stringify(names) : '';

/**
 * Walk `report.results` once and derive both the per-indicator entry details
 * (prefill values) and the set of indicators whose details are already fully
 * persisted server-side. Keeping this as a single helper avoids iterating
 * `report.results` twice and keeps the "what counts as saved" rule in one
 * place.
 */
const deriveDetailsFromResults = (
  results: ReportsTable['results'] | undefined,
): {
  details: Record<number, EntryDetails>;
  savedIds: Set<number>;
} => {
  const details: Record<number, EntryDetails> = {};
  const savedIds = new Set<number>();
  (results ?? []).forEach(r => {
    if (r?.indicatorId == null) return;
    const successCriteria = r.successCriteria ?? '';
    const forecast = r.forecastInitial != null ? String(r.forecastInitial) : '';
    details[r.indicatorId] = { successCriteria, forecast };
    if (successCriteria.trim().length > 0 && r.forecastInitial != null) {
      savedIds.add(r.indicatorId);
    }
  });
  return { details, savedIds };
};

const ReachIndicatorsField: React.FC<ReachIndicatorsFieldProps> = ({
  value,
  onChange,
  disabled,
  report,
  onBlockedChange,
}) => {
  const client = useClient();
  const { allReachIndicators, answers } = useProgram();
  const [sideSaveError, setSideSaveError] = useState<string | null>(null);

  const options = useMemo<IndicatorOption[]>(
    () =>
      (allReachIndicators?.options ?? []).map(opt => ({
        indicatorId: opt.id,
        name: opt.name,
        indicator: (opt as unknown) as Indicator,
      })),
    [allReachIndicators],
  );

  const optionByName = useMemo(() => {
    const map = new Map<string, IndicatorOption>();
    options.forEach(o => map.set(o.name, o));
    return map;
  }, [options]);

  // Selected names are the source of truth for the persisted answer.
  // Owned locally so per-click updates never get clobbered by a parent
  // effect resyncing `value` — we only re-seed when `value.text` changes
  // to a shape we did not just emit (initial load / external resets).
  const [selectedNames, setSelectedNames] = useState<string[]>(() =>
    parseReachIndicatorNames(value.text),
  );
  const lastSerializedRef = useRef<string>(serializeNames(selectedNames));

  useEffect(() => {
    const incoming = value.text ?? '';
    if (incoming === lastSerializedRef.current) return;
    setSelectedNames(parseReachIndicatorNames(incoming));
    lastSerializedRef.current = incoming;
  }, [value.text]);

  // Per-entry success criteria + forecast live only in component state; they
  // are persisted through the two side APIs (not through the answer text).
  // Seed both the prefill values and the "already saved" set from
  // `report.results` so previously-saved entries show up on reopen and the
  // user doesn't have to re-click "Save details" before "Save & Continue".
  const seededFromResults = useMemo(
    () => deriveDetailsFromResults(report?.results),
    [report?.results],
  );
  const [detailsByIndicatorId, setDetailsByIndicatorId] = useState<
    Record<number, EntryDetails>
  >(() => seededFromResults.details);

  const [savedIndicatorIds, setSavedIndicatorIds] = useState<Set<number>>(
    () => seededFromResults.savedIds,
  );
  const [savingIndicatorId, setSavingIndicatorId] = useState<number | null>(
    null,
  );

  const getDetails = (indicatorId: number): EntryDetails =>
    detailsByIndicatorId[indicatorId] || { successCriteria: '', forecast: '' };

  const patchDetails = (indicatorId: number, patch: Partial<EntryDetails>) => {
    setDetailsByIndicatorId(prev => ({
      ...prev,
      [indicatorId]: { ...getDetails(indicatorId), ...patch },
    }));
    // Any edit invalidates the previously-saved state for this entry and
    // must also blank the parent answer text so "Save & Continue" is gated
    // until the user re-saves the affected entry.
    setSavedIndicatorIds(prev => {
      if (!prev.has(indicatorId)) return prev;
      const next = new Set(prev);
      next.delete(indicatorId);
      return next;
    });
    // Selections still exist but this entry needs re-saving — emit the
    // "pending" sentinel so Save & Continue is blocked with a targeted msg.
    propagateAnswer(selectedNames, selectedNames.length > 0);
  };

  const hasImpactModel = Boolean(answers[40003]?.text);
  useBlockedSignal(!hasImpactModel, onBlockedChange);

  if (!hasImpactModel) {
    return (
      <TrueImpactText variant="body-small" color="critical">
        Please select your impact model in the previous question before choosing
        reach indicators.
      </TrueImpactText>
    );
  }

  if (options.length === 0) {
    return (
      <TrueImpactText variant="body-small" color="critical">
        No reach indicators are available for the selected impact model.
      </TrueImpactText>
    );
  }

  /**
   * Propagate the answer text to the parent. When all selected indicators are
   * saved we emit the JSON-serialized names (validation passes). When there
   * are selections that still need "Save details", we emit a sentinel so the
   * shared validator surfaces the correct message instead of the generic
   * "Select at least one reach indicator".
   */
  const propagateAnswer = (namesToPersist: string[], hasPending = false) => {
    const serialized = hasPending
      ? REACH_INDICATORS_UNSAVED_MARKER
      : serializeNames(namesToPersist);
    lastSerializedRef.current = serialized;
    onChange({ ...value, text: serialized });
  };

  const toggleIndicator = (opt: IndicatorOption) => {
    if (disabled) return;
    const isSelected = selectedNames.includes(opt.name);
    const next = isSelected
      ? selectedNames.filter(n => n !== opt.name)
      : [...selectedNames, opt.name];
    setSelectedNames(next);
    let nextSaved = savedIndicatorIds;
    if (isSelected && savedIndicatorIds.has(opt.indicatorId)) {
      nextSaved = new Set(savedIndicatorIds);
      nextSaved.delete(opt.indicatorId);
      setSavedIndicatorIds(nextSaved);
    }
    // Decide whether any newly-selected indicator still needs saving.
    const allSaved = next.every(name => {
      const o = optionByName.get(name);
      return o ? nextSaved.has(o.indicatorId) : false;
    });
    // When nothing is selected, emit '' → "Select at least one".
    // When selections exist but not all are saved, emit the sentinel.
    // When everything is saved (e.g. deselecting the only unsaved one), emit
    // the JSON list so Save & Continue can proceed.
    if (next.length === 0) {
      propagateAnswer([]);
    } else if (!allSaved) {
      propagateAnswer(next, true);
    } else {
      propagateAnswer(next);
    }
  };

  const saveEntry = async (opt: IndicatorOption) => {
    if (!client) return;
    const details = getDetails(opt.indicatorId);
    const forecastNum = Number(details.forecast);
    if (
      !details.successCriteria.trim() ||
      !details.forecast.trim() ||
      !Number.isFinite(forecastNum) ||
      forecastNum <= 0
    ) {
      setSideSaveError(
        `Enter success criteria and a forecast greater than 0 for "${opt.name}" before saving.`,
      );
      return;
    }
    setSideSaveError(null);
    setSavingIndicatorId(opt.indicatorId);
    try {
      await updateSuccessCriteria(
        client,
        report.id,
        opt.indicatorId,
        true,
        details.successCriteria,
      );
      await saveReportIndicators(client, {
        reportId: report.id,
        indicatorId: opt.indicatorId,
        forecast: details.forecast,
        finalForecast: null,
      });
      // `report.results` isn't re-fetched after these two side-API saves,
      // and `QuestionRenderer`'s `key={questionId}` remounts us on
      // navigate-back — so patch the results array in place (existing row
      // or synthetic insert) to keep the next remount's seed in sync.
      if (!report.results) {
        (report as { results: ReportsTable['results'] }).results = [];
      }
      const forecastNum = Number(details.forecast);
      const nextForecastInitial = Number.isFinite(forecastNum)
        ? forecastNum
        : null;
      const existing = report.results.find(
        r => r?.indicatorId === opt.indicatorId,
      );
      if (existing) {
        existing.successCriteria = details.successCriteria;
        if (nextForecastInitial != null) {
          existing.forecastInitial = nextForecastInitial;
        }
      } else {
        report.results.push(({
          indicatorId: opt.indicatorId,
          successCriteria: details.successCriteria,
          forecastInitial: nextForecastInitial,
        } as unknown) as ReportsTable['results'][number]);
      }
      setSavedIndicatorIds(prev => {
        const nextSet = new Set(prev);
        nextSet.add(opt.indicatorId);
        // If every selected indicator is now saved, push the answer text
        // upstream so Save & Continue can proceed.
        const allSaved = selectedNames.every(name => {
          const o = optionByName.get(name);
          return o ? nextSet.has(o.indicatorId) : true;
        });
        if (allSaved) propagateAnswer(selectedNames);
        return nextSet;
      });
    } catch {
      setSideSaveError(
        `Failed to save details for "${opt.name}". Please try again.`,
      );
    } finally {
      setSavingIndicatorId(null);
    }
  };

  // Preserve selection order so entry cards line up with the click order.
  const selectedOptions = selectedNames
    .map(name => optionByName.get(name))
    .filter((o): o is IndicatorOption => Boolean(o));

  return (
    <StyledDiv>
      <SelectorField>
        <SelectorOptions
          options={options}
          variant="checkbox"
          margin="0"
          maxWidth="100%"
          getLabel={opt => opt.name}
          getSelected={opt => selectedNames.includes(opt.name)}
          onClick={toggleIndicator}
        />
      </SelectorField>

      {selectedOptions.map(opt => {
        const details = getDetails(opt.indicatorId);
        const isSaved = savedIndicatorIds.has(opt.indicatorId);
        const isSaving = savingIndicatorId === opt.indicatorId;
        const forecastValue = Number(details.forecast);
        const canSave =
          !disabled &&
          !isSaving &&
          details.successCriteria.trim().length > 0 &&
          details.forecast.trim().length > 0 &&
          Number.isFinite(forecastValue) &&
          forecastValue > 0;
        return (
          <EntryCard key={opt.indicatorId}>
            <TrueImpactText variant="body-small-bold">
              {opt.name}
            </TrueImpactText>
            <FieldLabel>
              <TrueImpactText variant="body-small">
                What it means to your program
              </TrueImpactText>
              <TrueImpactTextfield
                id={`reach-criteria-${opt.indicatorId}`}
                name={`reach-criteria-${opt.indicatorId}`}
                placeholder=""
                isTextarea
                disabled={disabled}
                maxLength={SUCCESS_CRITERIA_MAX_LENGTH}
                value={details.successCriteria}
                onChange={next =>
                  patchDetails(opt.indicatorId, { successCriteria: next })
                }
              />
              <TrueImpactTextIndicator
                text={details.successCriteria}
                minLength={0}
                maxLength={SUCCESS_CRITERIA_MAX_LENGTH}
              />
            </FieldLabel>
            <FieldLabel>
              <TrueImpactText variant="body-small">Forecast</TrueImpactText>
              <TrueImpactTextfield
                id={`reach-forecast-${opt.indicatorId}`}
                name={`reach-forecast-${opt.indicatorId}`}
                type="number"
                placeholder=""
                disabled={disabled}
                value={details.forecast}
                onChange={next =>
                  patchDetails(opt.indicatorId, { forecast: next })
                }
              />
            </FieldLabel>
            <CardGuide
              text={getExampleText(opt.indicator, 'reach', undefined)}
              padding={GUIDE_PADDING}
            />
            <SaveRow>
              <TrueImpactButton
                variant={isSaved ? 'secondary-small' : 'primary-small'}
                disabled={!canSave}
                onClick={() => saveEntry(opt)}
              >
                {isSaving ? 'Saving…' : isSaved ? 'Saved ✓' : 'Save details'}
              </TrueImpactButton>
            </SaveRow>
          </EntryCard>
        );
      })}

      {sideSaveError && (
        <TrueImpactText variant="body-small" color="critical">
          {sideSaveError}
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default ReachIndicatorsField;
